import React, { useState, useEffect } from 'react';
import { X, Building2, Upload, Image as ImageIcon, Eye, Edit, Trash2, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import Button from './ui/Button';
import { API_ENDPOINTS, API_BASE_URL } from '../utils/config';
import { getMediaUrl, getAuthenticatedImageUrl } from '../utils/imageUtils';

const ProfileModal = ({ isOpen, onClose, user }) => {
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [originalLogoUrl, setOriginalLogoUrl] = useState(null); // Track original logo URL from API
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    gstin: '',
    pan: '',
    phone: '',
    email: '',
    website: '',
    accountName: '',
    bankName: '',
    accountNo: '',
    ifscCode: '',
    branch: 'Main'
  });
  
  // Only one company profile is allowed
  const [companyProfiles, setCompanyProfiles] = useState([]);

  // Fetch company profiles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCompanyProfiles();
    }
  }, [isOpen]);

  const fetchCompanyProfiles = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers });
      
      // API might return an array or a single object
      const profiles = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setCompanyProfiles(profiles);
    } catch (error) {
      // Error fetching profiles handled silently
      setCompanyProfiles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProfileDetails = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.COMPANY_PROFILE_DETAIL(id), { headers });
      return response.data;
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to fetch profile details';
      setErrorMessage(apiError);
      return null;
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setOriginalLogoUrl(null); // Clear original logo when user removes it
  };

  const handleAddProfile = () => {
    // Only allow adding if no profile exists
    if (companyProfiles.length === 0) {
    setEditingProfile(null);
    setViewingProfile(null);
    setShowForm(true);
    setLogoPreview(null);
    setLogoFile(null);
      setOriginalLogoUrl(null);
    setFormData({
      companyName: '',
      address: '',
      gstin: '',
      pan: '',
      phone: '',
      email: '',
      website: '',
      accountName: '',
      bankName: '',
      accountNo: '',
      ifscCode: '',
      branch: 'Main'
    });
    }
  };

  const handleEditProfile = async (profile) => {
    setEditingProfile(profile);
    setViewingProfile(null);
    setShowForm(true);
    
    // Fetch full profile details
    const fullProfile = await fetchProfileDetails(profile.id);
    const profileData = fullProfile || profile;
    
    // Map API fields to form fields (handle both camelCase and snake_case)
    setFormData({
      companyName: profileData.company_name || profileData.companyName || '',
      address: profileData.address || '',
      gstin: profileData.gstin || profileData.gst_in || '',
      pan: profileData.pan || '',
      phone: profileData.phone_number || profileData.phone || '',
      email: profileData.email || '',
      website: profileData.website || '',
      accountName: profileData.account_name || profileData.accountName || '',
      bankName: profileData.bank_name || profileData.bankName || '',
      accountNo: profileData.account_no || profileData.accountNo || profileData.account_number || '',
      ifscCode: profileData.ifsc_code || profileData.ifscCode || '',
      branch: profileData.branch || 'Main'
    });
    
    // Handle logo - check for company_logo first, then logo
    // company_logo can be null, a URL string, or a file path
    if (profileData.company_logo && profileData.company_logo !== null && profileData.company_logo !== '') {
      const logoUrl = String(profileData.company_logo);
      // Store original URL for reference
      setOriginalLogoUrl(logoUrl);
      // Use centralized utility to normalize URL and add authentication
      const fullLogoUrl = getAuthenticatedImageUrl(logoUrl);
      setLogoPreview(fullLogoUrl);
    } else if (profileData.logo && profileData.logo !== null && profileData.logo !== '') {
      const logoUrl = String(profileData.logo);
      setOriginalLogoUrl(logoUrl);
      // Use centralized utility to normalize URL and add authentication
      const fullLogoUrl = getAuthenticatedImageUrl(logoUrl);
      setLogoPreview(fullLogoUrl);
    } else {
      setLogoPreview(null);
      setOriginalLogoUrl(null);
    }
    setLogoFile(null);
  };

  const handleViewProfile = async (profile) => {
    // Fetch full profile details for viewing
    const fullProfile = await fetchProfileDetails(profile.id);
    setViewingProfile(fullProfile || profile);
    setEditingProfile(null);
    setShowForm(false);
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Are you sure you want to delete this company profile?')) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      await axios.delete(API_ENDPOINTS.COMPANY_PROFILE_DELETE(profileId), { headers });
      
      setSuccessMessage('Company profile deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh the list
      await fetchCompanyProfiles();
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      'Failed to delete company profile';
      setErrorMessage(apiError);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingProfile(null);
    setLogoPreview(null);
    setLogoFile(null);
    setOriginalLogoUrl(null);
    setFormData({
      companyName: '',
      address: '',
      gstin: '',
      pan: '',
      phone: '',
      email: '',
      website: '',
      accountName: '',
      bankName: '',
      accountNo: '',
      ifscCode: '',
      branch: 'Main'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Prepare FormData for file upload
      const formDataToSend = new FormData();
      
      // Map form fields to API fields (using snake_case as standard)
      formDataToSend.append('company_name', formData.companyName || '');
      formDataToSend.append('address', formData.address || '');
      formDataToSend.append('gstin', formData.gstin || '');
      formDataToSend.append('pan', formData.pan || '');
      formDataToSend.append('phone_number', formData.phone || '');
      formDataToSend.append('email', formData.email || '');
      formDataToSend.append('website', formData.website || '');
      formDataToSend.append('account_name', formData.accountName || '');
      formDataToSend.append('bank_name', formData.bankName || '');
      formDataToSend.append('account_number', formData.accountNo || '');
      formDataToSend.append('ifsc_code', formData.ifscCode || '');
      formDataToSend.append('branch', formData.branch || 'Main');
      
      // Append logo file if a new one was selected - use company_logo field name
      if (logoFile) {
        // New logo file selected - send it
        formDataToSend.append('company_logo', logoFile);
      } else if (editingProfile && originalLogoUrl && !logoPreview && !logoFile) {
        // User removed the existing logo - send empty string to clear it
        // Backend should handle empty string as null/clear logo
        formDataToSend.append('company_logo', '');
      }
      // If no logoFile and logo wasn't removed, don't send company_logo field
      // This allows backend to keep existing logo unchanged

      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      if (editingProfile) {
        // Update existing profile - use PATCH for partial update (better for FormData)
        const response = await axios.patch(
          API_ENDPOINTS.COMPANY_PROFILE_PATCH(editingProfile.id),
          formDataToSend,
          { headers }
        );
        
        setSuccessMessage('Company profile updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Refresh the list
        await fetchCompanyProfiles();
      } else {
        // Create new profile (only if none exists)
        if (companyProfiles.length === 0) {
          const response = await axios.post(
            API_ENDPOINTS.COMPANY_PROFILE_CREATE,
            formDataToSend,
            { headers }
          );
          
          setSuccessMessage('Company profile created successfully!');
          setTimeout(() => setSuccessMessage(''), 3000);
          
          // Refresh the list
          await fetchCompanyProfiles();
        }
      }
      
    setShowForm(false);
    setEditingProfile(null);
    setLogoPreview(null);
    setLogoFile(null);
      setOriginalLogoUrl(null);
    } catch (error) {
      // Log full error for debugging
      console.error('Error saving company profile:', error);
      console.error('Error response:', error?.response?.data);
      
      // Extract error message - check multiple possible locations
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.response?.data?.error ||
                      (typeof error?.response?.data === 'object' && error?.response?.data !== null 
                        ? JSON.stringify(error.response.data) 
                        : null) ||
                      error?.message || 
                      'Failed to save company profile';
      setErrorMessage(apiError);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .profile-modal-scroll::-webkit-scrollbar {
          width: 10px;
        }
        .profile-modal-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .profile-modal-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .profile-modal-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col my-4 sm:my-8 relative overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0 sticky top-0 z-[100] rounded-t-lg shadow-sm">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">Profile</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 relative z-[101] flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto profile-modal-scroll" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
            {/* Company Profile Content */}
            <div className="p-4 sm:p-6">
              <div className="space-y-6">
                {/* Success/Error Messages */}
                {successMessage && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
                    {successMessage}
                  </div>
                )}
                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                    {errorMessage}
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Company Profile</h3>
                    {!showForm && (
                      <Button 
                        onClick={handleAddProfile} 
                        iconName="Plus"
                        disabled={companyProfiles.length > 0 || isLoading}
                      >
                        Add Company Profile
                      </Button>
                    )}
                  </div>

                  {/* Company Profile Form */}
                  {showForm && (
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
                      <div className="space-y-4">
                      {/* Logo Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0">
                            {logoPreview ? (
                              <div className="relative">
                                <img
                                  src={logoPreview}
                                  alt="Company Logo Preview"
                                  className="w-32 h-32 object-contain border border-gray-300 rounded-lg p-2 bg-white"
                                />
                                <button
                                  type="button"
                                  onClick={handleRemoveLogo}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                                <ImageIcon className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoChange}
                                className="hidden"
                                id="logo-upload"
                              />
                              <div className="flex flex-col space-y-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => document.getElementById('logo-upload')?.click()}
                                  className="w-full sm:w-auto"
                                >
                                  <Upload className="w-4 h-4 mr-2" />
                                  {logoPreview ? 'Change Logo' : 'Upload Logo'}
                                </Button>
                                <p className="text-xs text-gray-500">
                                  Recommended: PNG, JPG or SVG (max 2MB)
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Enter company name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                        <textarea
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Enter company address"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">GSTIN</label>
                          <input
                            type="text"
                            name="gstin"
                            value={formData.gstin}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Enter GSTIN"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">PAN</label>
                          <input
                            type="text"
                            name="pan"
                            value={formData.pan}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Enter PAN"
                          />
                        </div>
                      </div>
                      
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Enter phone number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Enter email"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Enter website URL"
                        />
                      </div>

                      {/* Bank Details Section */}
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="text-base font-semibold text-foreground mb-4">Bank Details</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                            <input
                              type="text"
                              name="accountName"
                              value={formData.accountName}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              placeholder="Enter account name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                            <input
                              type="text"
                              name="bankName"
                              value={formData.bankName}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              placeholder="Enter bank name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Account No</label>
                            <input
                              type="text"
                              name="accountNo"
                              value={formData.accountNo}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              placeholder="Enter account number"
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                              <input
                                type="text"
                                name="ifscCode"
                                value={formData.ifscCode}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Enter IFSC code"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
                              <input
                                type="text"
                                name="branch"
                                value={formData.branch}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Enter branch"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                        <div className="flex gap-3">
                          <Button onClick={handleSaveProfile} disabled={isSubmitting} loading={isSubmitting}>
                            {editingProfile ? 'Update Company Profile' : 'Save Company Profile'}
                          </Button>
                          <Button variant="outline" onClick={handleCancelForm} disabled={isSubmitting}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Company Profiles Data Table */}
                  {!showForm && (
                    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
                      {isLoading ? (
                        <div className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center space-y-3">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                            <p className="text-muted-foreground">Loading company profiles...</p>
                          </div>
                        </div>
                      ) : (
                        <>
                      {/* Desktop Table View */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full table-auto">
                          <thead className="bg-muted/50 border-b-2 border-border">
                            <tr>
                              <th className="text-left px-4 sm:px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">
                                Company Name
                              </th>
                              <th className="text-left px-4 sm:px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">
                                GSTIN
                              </th>
                              <th className="text-left px-4 sm:px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">
                                Email
                              </th>
                              <th className="text-left px-4 sm:px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">
                                Phone
                              </th>
                              <th className="text-center px-4 sm:px-6 py-4 font-semibold text-sm text-foreground uppercase tracking-wide">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {companyProfiles.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="px-6 py-12 text-center">
                                  <div className="flex flex-col items-center justify-center space-y-3">
                                    <Building2 className="w-12 h-12 text-muted-foreground" />
                                    <p className="text-muted-foreground text-lg">No company profiles found</p>
                                    <p className="text-sm text-muted-foreground">Add your first company profile to get started</p>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                                  companyProfiles.map((profile) => {
                                    const companyName = profile.company_name || profile.companyName || '';
                                    const gstin = profile.gstin || profile.gst_in || '';
                                    const email = profile.email || '';
                                    const phone = profile.phone_number || profile.phone || '';
                                    
                                    return (
                                <tr key={profile.id} className="border-b border-border hover:bg-muted/20 transition-all duration-150">
                                  <td className="px-4 sm:px-6 py-4">
                                          <div className="font-semibold text-foreground">{companyName}</div>
                                  </td>
                                  <td className="px-4 sm:px-6 py-4">
                                          <div className="text-foreground text-sm">{gstin}</div>
                                  </td>
                                  <td className="px-4 sm:px-6 py-4">
                                          <div className="text-foreground text-sm">{email}</div>
                                  </td>
                                  <td className="px-4 sm:px-6 py-4">
                                          <div className="text-foreground text-sm">{phone}</div>
                                  </td>
                                  <td className="px-4 sm:px-6 py-4">
                                    <div className="flex items-center justify-center space-x-2">
                                      <button
                                        onClick={() => handleViewProfile(profile)}
                                        className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
                                        title="View details"
                                              disabled={isLoading}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleEditProfile(profile)}
                                        className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
                                        title="Edit profile"
                                              disabled={isLoading}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteProfile(profile.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                        title="Delete profile"
                                              disabled={isLoading}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                    );
                                  })
                            )}
                          </tbody>
                        </table>
                      </div>
                        </>
                      )}
                      
                      {/* Mobile Card View */}
                      {!isLoading && (
                      <div className="md:hidden">
                        {companyProfiles.length === 0 ? (
                          <div className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center justify-center space-y-3">
                              <Building2 className="w-12 h-12 text-muted-foreground" />
                              <p className="text-muted-foreground text-lg">No company profiles found</p>
                              <p className="text-sm text-muted-foreground">Add your first company profile to get started</p>
                            </div>
                          </div>
                        ) : (
                            companyProfiles.map((profile) => {
                              const companyName = profile.company_name || profile.companyName || '';
                              const gstin = profile.gstin || profile.gst_in || '';
                              const email = profile.email || '';
                              const phone = profile.phone_number || profile.phone || '';
                              
                              return (
                            <div key={profile.id} className="border-b border-border p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                      <div className="font-semibold text-foreground">{companyName}</div>
                                      <div className="text-sm text-muted-foreground mt-1">{gstin}</div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleViewProfile(profile)}
                                    className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
                                    title="View details"
                                        disabled={isLoading}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleEditProfile(profile)}
                                    className="p-2 text-primary hover:bg-primary/10 rounded-md transition-colors"
                                    title="Edit profile"
                                        disabled={isLoading}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProfile(profile.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    title="Delete profile"
                                        disabled={isLoading}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Email: </span>
                                      <span className="text-foreground">{email}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Phone: </span>
                                      <span className="text-foreground">{phone}</span>
                                </div>
                              </div>
                            </div>
                              );
                            })
                        )}
                      </div>
                      )}
                    </div>
                  )}

                  {/* View Profile Modal */}
                  {viewingProfile && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
                          <h3 className="text-lg font-semibold text-foreground">Company Profile Details</h3>
                          <button
                            onClick={() => setViewingProfile(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="p-6 space-y-6">
                          {/* Company Logo Display */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                            {(() => {
                              const logoUrl = viewingProfile.company_logo || viewingProfile.logo;
                              let fullLogoUrl = null;
                              
                              if (logoUrl && logoUrl !== null && logoUrl !== '') {
                                const logoUrlString = String(logoUrl);
                                if (logoUrlString.startsWith('http://') || logoUrlString.startsWith('https://')) {
                                  fullLogoUrl = logoUrlString;
                                } else if (logoUrlString.startsWith('/')) {
                                  const baseUrl = API_BASE_URL.replace('/api', '');
                                  fullLogoUrl = `${baseUrl}${logoUrlString}`;
                                } else {
                                  const baseUrl = API_BASE_URL.replace('/api', '');
                                  fullLogoUrl = `${baseUrl}/${logoUrlString}`;
                                }
                              }
                              
                              return fullLogoUrl ? (
                                <div className="flex justify-center">
                                  <img
                                    src={fullLogoUrl}
                                    alt="Company Logo"
                                    className="w-32 h-32 object-contain border border-gray-300 rounded-lg p-2 bg-white"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  {/* Error fallback */}
                                  <div 
                                    className="hidden items-center justify-center w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50"
                                    style={{ display: 'none' }}
                                  >
                                    <ImageIcon className="w-8 h-8 text-gray-400" />
                                  </div>
                                </div>
                              ) : (
                                <div className="flex justify-center">
                                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                                    <ImageIcon className="w-8 h-8 text-gray-400" />
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                              <p className="text-foreground">{viewingProfile.company_name || viewingProfile.companyName || '-'}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                              <p className="text-foreground">{viewingProfile.gstin || viewingProfile.gst_in || '-'}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                              <p className="text-foreground">{viewingProfile.pan || '-'}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <p className="text-foreground">{viewingProfile.email || '-'}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                              <p className="text-foreground">{viewingProfile.phone_number || viewingProfile.phone || '-'}</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                              <p className="text-foreground">{viewingProfile.website || '-'}</p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                            <p className="text-foreground">{viewingProfile.address || '-'}</p>
                          </div>
                          <div className="pt-4 border-t border-gray-200">
                            <h4 className="text-base font-semibold text-foreground mb-4">Bank Details</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                                <p className="text-foreground">{viewingProfile.account_name || viewingProfile.accountName || '-'}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                                <p className="text-foreground">{viewingProfile.bank_name || viewingProfile.bankName || '-'}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Account No</label>
                                <p className="text-foreground">{viewingProfile.account_no || viewingProfile.accountNo || viewingProfile.account_number || '-'}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                                <p className="text-foreground">{viewingProfile.ifsc_code || viewingProfile.ifscCode || '-'}</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                                <p className="text-foreground">{viewingProfile.branch || '-'}</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <Button variant="outline" onClick={() => setViewingProfile(null)}>
                              Close
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileModal;

